import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useFela } from 'react-fela';
import { Text, Box } from 'components';
import { I18nContext } from 'components/I18n';
import { responsiveLg, responsiveMd } from 'styles/fela/mixins';
import { GlobalTheme } from 'types';
import { ComponentRuleProps, RuleStyles } from 'styles/fela/createComponent';

const ReactPinField = React.lazy(() => import('react-pin-field'));

const inputRules = ({ theme }: ComponentRuleProps): RuleStyles => ({
  border: 'none',
  borderBottom: `4px solid ${theme.color.primary}`,
  borderRadius: 0,
  backgroundColor: 'transparent',
  width: 32,
  outline: 'none',
  margin: '0 5px',
  padding: 0,
  fontSize: '1.875rem',
  color: theme.color.primary,
  textAlign: 'center',
  fontWeight: 'bold',
  ':placeholder-shown': {
    opacity: 0.4,
  },
  ':focus': {
    opacity: 1,
  },
  ':invalid': {
    borderBottom: `4px solid ${theme.color.danger}`,
  },
  extend: [
    {
      condition: true,
      style: responsiveMd({
        width: 60,
        fontSize: '3.125rem',
        margin: '0 10px',
      }),
    },
    {
      condition: true,
      style: responsiveLg({
        width: 80,
        fontSize: '5rem',
        margin: '0 14px',
      }),
    },
  ],
});

type UserCodeInputProps = {
  length?: number,
  onComplete?: (code: string) => void,
  onChange?: (code: string) => void,
  initValue?: string,
  invalid?: boolean,
};

const UserCodeInput = (
  { length = 8, onComplete, onChange, initValue, invalid }: UserCodeInputProps,
): JSX.Element => {
  const { css } = useFela<GlobalTheme>();
  const i18n = useContext(I18nContext);
  const ref = useRef<HTMLInputElement[] | null>(null);

  useEffect(() => {
    if (!invalid) return;

    ref.current?.forEach((input) => {
      input.value = '';
      input.setCustomValidity('Invalid');
    });
    ref.current?.[0].focus();
  }, [invalid]);

  const handleChange = (code: string) => {
    ref.current?.forEach((input) => {
      input.setCustomValidity('');
    });
    onChange?.(code);
  };

  const handleRect = useCallback((node: HTMLInputElement[]) => {
    ref.current = node;

    ref.current?.forEach((input, idx) => {
      input.setAttribute(
        'aria-label',
        i18n.formatText('aria.deviceAuth.codeCharacter', { index: idx + 1, total: length }),
      );
      if (initValue) input.value = initValue[idx];
    });
  }, [i18n, length, initValue]);

  useEffect(() => {
    if (initValue && initValue.length === length) {
      onComplete?.(initValue);
    }
  }, []);

  return (
    <React.Suspense fallback={<Text id="loading" />}>
      <Box flexBox flexDirection="column" alignItems="center">
        <div id="code-inputs">
          <ReactPinField
            ref={handleRect}
            length={length}
            onComplete={onComplete}
            onChange={handleChange}
            className={css(inputRules)}
            placeholder=" "
            format={k => k.toUpperCase()}
          />
        </div>
        <Box mt="medium">
          {
            invalid
              ? <Text color="danger" fontSize="smedium" id="deviceAuth.invalidCode" />
              : <Text>&nbsp;</Text>
          }
        </Box>
      </Box>
    </React.Suspense>
  );
};

export default UserCodeInput;
